import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import type { AmazonCoupon, AmazonCouponStatus } from '@staff-app/shared-types';
import { CopyButton } from '@/components/CopyButton';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from '@/lib/data-table';
import { formatDiscount } from '../hooks/useAmazonCoupons';

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleDateString();
}

const MARKET_LABEL: Record<string, string> = { us: 'US', uk: 'UK', de: 'DE' };

/**
 * Staff email → the part before the @.
 *
 * Only `@optisigns.com` can sign in to this console, so the domain is the same on every row and
 * spends width saying nothing. ONLY for STAFF addresses — the Customer column keeps its domain,
 * where the domain is the interesting half and shortening it would be a lie about who was mailed.
 *
 * Anything without an `@` is passed through unchanged rather than guessed at.
 */
function staffName(email?: string | null): string {
  if (!email) return '—';
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

/**
 * Status badge. FOUR states, and the fourth is the reason this component exists separately from
 * DemoCouponTable rather than being parameterised out of it.
 *
 * THREE states, and "Expired" now means exactly one thing: the campaign ended, so Amazon refuses
 * the code. There used to be a fourth for a 30-day date of our own that nothing enforced, which
 * made "Expired" ambiguous and every surface explain it away.
 */
function StatusBadge({ status }: { status: AmazonCouponStatus }) {
  const variant = status === 'USED' ? 'secondary' : status === 'EXPIRED' ? 'destructive' : 'default';
  return (
    <Badge variant={variant as never} data-testid={`amazon-status-${status}`}>
      {status === 'EXPIRED' ? 'Expired' : status === 'USED' ? 'Used' : 'Active'}
    </Badge>
  );
}

/**
 * The Redeemed cell.
 *
 * Three states, and the distinction between the last two is the whole point:
 *   never reconciled  — Amazon has not been asked yet. NOT the same as "not redeemed".
 *   all spent         — redemptions >= codes allocated, so this code is certainly one of them.
 *   partial           — N of M redeemed on the promotion; which codes is unknowable.
 */
function RedeemedCell({ coupon }: { coupon: AmazonCoupon }) {
  const redeemed = coupon.promotionRedemptionCount;
  const allocated = coupon.promotionAllocatedCount ?? 0;
  const synced = coupon.promotionReconciledAt ? fmtDate(coupon.promotionReconciledAt) : null;

  if (redeemed == null) {
    return (
      // Terse on purpose (requester, 2026-09-24): the column is narrow and this is the resting
      // state of every row until the daily sync runs. Still "Not yet" rather than a dash, because
      // a dash would read as "not redeemed" — a claim nobody has established.
      <div className="whitespace-normal" data-testid={`amazon-redeemed-${coupon.code}`}>
        <span className="text-sm text-muted-foreground">{'Not yet'}</span>
      </div>
    );
  }

  const allSpent = allocated > 0 && redeemed >= allocated;
  return (
    <div className="whitespace-normal" data-testid={`amazon-redeemed-${coupon.code}`}>
      {allSpent ? (
        <Badge variant={'secondary' as never} data-testid={`amazon-redeemed-used-${coupon.code}`}>
          {'Used'}
        </Badge>
      ) : (
        <span className="text-sm text-muted-foreground">{'—'}</span>
      )}
      <div className="text-xs text-muted-foreground" data-testid={`amazon-promo-tally-${coupon.code}`}>
        {allSpent
          ? `all ${allocated} redeemed on this promo`
          : `${redeemed} of ${allocated} redeemed on this promo`}
        {synced ? ` · Amazon, ${synced}` : ''}
      </div>
    </div>
  );
}

/**
 * The Amazon-coupon ledger. ALLOCATED codes only — a pooled code was given to nobody.
 *
 * Built on shadcn `Table` like its neighbours, but the PAGE wraps it in `DATA_TABLE_WRAPPER`,
 * which `OrderTaskTable` and `DemoCouponTable` both omit — CHANGELOG 2026-08-26 records that both
 * therefore have non-sticky headers, a latent bug it deliberately left alone. This table is new,
 * so it starts correct instead of inheriting it.
 */
export function AmazonCouponTable({ coupons }: { coupons: AmazonCoupon[] }) {
  if (!coupons.length) {
    return (
      <p className="p-6 text-sm text-muted-foreground" data-testid="amazon-coupons-empty">
        {'No Amazon coupons issued yet.'}
      </p>
    );
  }

  return (
    <Table className={DATA_TABLE_CELLS} data-testid="amazon-coupons-table">
      <TableHeader className={DATA_TABLE_HEADER}>
        <TableRow>
          <TableHead>{'Code'}</TableHead>
          <TableHead>{'Market'}</TableHead>
          <TableHead>{'Device'}</TableHead>
          <TableHead className="text-right">{'Discount'}</TableHead>
          <TableHead>{'Customer'}</TableHead>
          <TableHead>{'Reason'}</TableHead>
          <TableHead>{'Issued by'}</TableHead>
          <TableHead>{'Issued'}</TableHead>
          <TableHead>{'Expires'}</TableHead>
          <TableHead>{'Status'}</TableHead>
          <TableHead>{'Redeemed'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {coupons.map((c) => (
          <TableRow key={c.code} data-testid={`amazon-coupon-row-${c.code}`}>
            {/* Copying the code is the whole point of finding a row — a staffer is here because
                a customer asked about theirs. The shared CopyButton rather than an eighth
                hand-rolled clipboard call, as its docblock asks. */}
            <TableCell className="font-mono">
              <span className="inline-flex items-center gap-1">
                {c.code}
                <CopyButton
                  value={c.code}
                  label={`Copy ${c.code}`}
                  testId={`amazon-copy-code-${c.code}`}
                  stopPropagation
                />
              </span>
            </TableCell>
            <TableCell data-testid="amazon-cell-market">
              {MARKET_LABEL[c.marketplace] ?? c.marketplace.toUpperCase()}
            </TableCell>
            {/* The DEVICE, with the SKU under it. The SKU is still what identifies the listing —
                ASIN cannot, since ProMax is B0GC1KZQZ2 in all three stores — but nobody reads
                "ARD-0002" and thinks "OptiStick", and the row exists to be read. */}
            <TableCell data-testid="amazon-cell-sku">
              <div>{c.deviceName ?? c.sku}</div>
              {c.deviceName ? (
                <div className="font-mono text-xs text-muted-foreground">{c.sku}</div>
              ) : null}
            </TableCell>
            {/* Amount AND currency together, always: ten pounds off is not ten dollars off, and a
                bare number is the thing a staffer would quote wrongly. */}
            <TableCell className="text-right font-mono" data-testid="amazon-cell-discount">
              {formatDiscount(c.discountAmount, c.currency)}
            </TableCell>
            <TableCell data-testid="amazon-cell-customer">{c.customerEmail || '—'}</TableCell>
            {/* Not truncated to a tooltip: on a path with no approver this is the only account of
                why the code exists. Same call DemoCouponTable made. */}
            <TableCell className="max-w-[24rem] whitespace-normal">{c.reason}</TableCell>
            {/* Domain dropped: every staff address is @optisigns.com, so it is width spent on
                nothing. The Customer cell above keeps its domain on purpose. */}
            <TableCell data-testid="amazon-cell-issuer" title={c.createdBy ?? undefined}>
              {staffName(c.createdBy)}
            </TableCell>
            <TableCell>{fmtDate(c.createdAt)}</TableCell>
            {/* THE campaign's end date, and only that (requester, 2026-09-24). This column used
                to carry a 30-day date of our own above it, which Amazon had never heard of and
                every row then had to explain. One date, one meaning. */}
            <TableCell data-testid={`amazon-expiry-${c.code}`}>
              {fmtDate(c.campaignEndsAt)}
            </TableCell>
            <TableCell>
              <StatusBadge status={c.status} />
            </TableCell>
            <TableCell>
              {/* DERIVED, never asserted. The manual Mark-used button that stood here was
                  removed (requester, 2026-09-24 — "they don't know"): CS has no way to learn that
                  a customer redeemed a code, so it was a control nobody could operate correctly.

                  Amazon reports redemptions per PROMOTION — it does not put the claim code on an
                  order — so the only per-code statement that can be made honestly is the one case
                  where the promotion's redemptions have reached the number of codes allocated:
                  then every allocated code is spent, this one included. Below that, the row shows
                  the promotion figure and says so. */}
              <RedeemedCell coupon={c} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
